import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { scrollToElement, scrollToPageTop } from '@/utils/scroll';
import { updateFilterString } from '@/utils/filterUtils';
import { getCurrentProductItem } from '@/utils/productUtils';
import { getSelectedFiltersInfos, isNote, parseFilters } from '@/utils/categUtils';

import { useServices } from '@/services/useServices';
import { usePrimeurUser } from '@/hooks';

import { GetServerSidePropsContext } from 'next';
import { NS } from '@/constants';

import { useAppDispatch, useAppSelector } from '@/redux';
import { selectStoreConf, selectStoreId } from '@/redux/slices/root.slice';
import { showStorageFilterModal } from '@/redux/slices/storage.slice';
import { useScreenSizeContext } from '@/components/ScreenSize.context';

import TopCategory from '@/components/categ/TopCategory';
import SortDesktop from '@/components/categ/SortDesktop';
import SortMobile from '@/components/categ/filters/SortMobile';
import CategFooter from '@/components/categ/winebox/CategFooter';
import CategHeader from '@/components/categ/winebox/CategHeader';
import ProductListCU from '@/components/categ/CategCU/ProductListCU';
import BreadcrumbContainer from '@/components/common/BreadcrumbContainer';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import SelectedFilters from '@/components/categ/CategCU/SelectedFilters';
import CategFilters from '@/components/categ/winebox/CategFilters';
import { Toggle } from '@/components/common/design-system/Toggle.ds';

import { DEFAULT_PAGE, DEFAULT_SORT, FILTERS, PAGE_SIZE } from '@/constants/categ';
import { SEO_TYPES } from '@/constants/seo';
import { ATTRIBUTES } from '@/constants/product';
import { URLS } from '@/constants/urls';

import styles from './CategViewGrid.module.scss';
import ModalAlertPrimeurInfos from '@/components/primeurAlert/ModalAlertPrimeurInfos';
import { useVisibleProducts } from '@/hooks/useVisibleProducts';

const Pagination = dynamic(() => import('@/components/categ/Pagination'), { ssr: false });
Pagination.displayName = 'Pagination';
const FilterMobileLike = dynamic(() => import('@/components/categ/filters/FilterMobileLike'), { ssr: false });
FilterMobileLike.displayName = 'FilterMobileLike';

export const CategViewGrid = ({ products, categ, categId, nbTotalRows, page, sort, topCategs, hashUserFromQuery }) => {
  const router = useRouter();
  const { t } = useTranslation([NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER]);
  const { datalayerService, productsService } = useServices();
  const dispatch = useAppDispatch();
  const isMobile = useScreenSizeContext().isMobile;
  const isMobileDevice = useScreenSizeContext().isMobileOrTablet;
  const isDesktop = useScreenSizeContext().isDesktop;

  const storeId = useAppSelector(selectStoreId);
  const storeConf = useAppSelector(selectStoreConf);
  const baseUrl = useAppSelector(({ root }) => root.baseUrl);
  const url = useAppSelector(({ root }) => root.storeConf.url);
  const locale = useAppSelector(({ root }) => root.storeConf.locale);
  const currency = useAppSelector(({ root }) => root.storeConf.currency);
  const storeIdForPrice = useAppSelector(({ gui }) => gui.storeIdForPrice);
  const isSortMobileVisible = useAppSelector(({ gui }) => gui.isSortMobileVisible);
  const isFilterMobileVisible = useAppSelector(({ gui }) => gui.isFilterMobileVisible);
  const alertPrimeurInfosModalVisible = useAppSelector(({ gui }) => gui.alertPrimeurInfosModalVisible);

  const descriptionRef = useRef<HTMLDivElement>();
  const topListingRef = useRef();
  const query = useSearchParams();

  const pageSize = PAGE_SIZE.LIST_CU;

  const [viewData, setViewData] = useState<any>({ products, categ, topCategs, nbTotalRows, page, sort });
  const [filterQuery, setFilterQuery] = useState<string>(query?.get('filter') || '');
  const [isSwitchDisable, setIsSwitchDisable] = useState<boolean>(categ.isSpirit);
  const [selectedFiltersInfos, setSelectedFiltersInfos] = useState([]);
  const onlyCUChecked = filterQuery.includes('conditionnement:onlyCU');

  const isCUCategory = categId?.includes(SEO_TYPES.CATEG_CU_PREFIX_ID);

  usePrimeurUser({ hashUserFromQuery });

  const visibilityTracking = useVisibleProducts(
    datalayerService,
    viewData.products || [],
    storeConf,
    viewData.categ,
    filterQuery,
    viewData.sort,
    undefined
  );

  useEffect(() => {
    const computeFilter = async () => {
      if (filterQuery && filterQuery !== '') {
        await refreshView({
          page: viewData.page || 1, sort: viewData.sort, filterQuery, onlyCU: onlyCUChecked, keepExtraParams: true
        });
      }
    };

    void computeFilter();
  }, []);

  useEffect(() => {
    scrollToPageTop();
  }, [page]);

  useEffect(() => {
    const isClient = typeof window !== 'undefined';
    const search = isClient ? window.location.search : '';
    setIsSwitchDisable(filterQuery?.includes(FILTERS.FORMAT) || search.includes(FILTERS.FORMAT) || categ.isSpirit);
  }, [filterQuery]);

  useEffect(() => {
    if (categ && typeof window !== 'undefined') {
      updateDatalayer();
    }
  }, []);

  const updateDatalayer = () => {
    datalayerService.updateCategDatalayer(
      products, filterQuery, sort, categ?.sortOrder,
      categ, false, storeConf.storeId, categ?.nbTotalRows, ''
    );
  };

  const refreshView = async ({ page, sort, filterQuery, onlyCU, keepExtraParams = false }: any) => {
    scrollToElement(topListingRef, { behavior: 'instant', block: 'start', inline: 'start' });

    const { slug: _slug, spiritSlug: _spiritSlug, page: _page, sort: _sort, filter: _filter, ...extraQuery } = router.query;
    const extraParams = keepExtraParams ? new URLSearchParams(extraQuery as Record<string, string>).toString() : '';

    const queryParams = [
      page !== 1 && `page=${page}`,
      sort !== 1 && `sort=${sort}`,
      filterQuery !== '' && `filter=${filterQuery}`,
      extraParams,
    ].filter((p) => !!p).join('&');

    let url: string = router.asPath.split('?')[0];

    if (queryParams) {
      url += `?${queryParams}`;
    }

    await router.push(url, url, { shallow: true });

    const functionToCall = isCUCategory ?
      productsService.getAllBottlesProductAndCategForListingCU.bind(productsService) :
      productsService.getCategWithFiltersAndProducts.bind(productsService);

    functionToCall(
      {
        storeId, categ: categId, page: page || 1, size: PAGE_SIZE.LIST_CU,
        filter: filterQuery, sort, withFilter: 'true',
        isCU: isCUCategory || onlyCU,
      },
      storeIdForPrice
    ).then((result: any) => {
      setViewData({ ...result, sort: parseInt(sort), page: parseInt(page), filters: result.filters.filters });
      setSelectedFiltersInfos(
        getSelectedFiltersInfos(t, filterQuery, parseFilters(result.filters, filterQuery, storeId), locale, currency) || []
      );
      setFilterQuery(filterQuery);
      dispatch(showStorageFilterModal());
    });
  };

  useEffect(() => {
    productsService.getFilters({ storeId, categId, filter: '' }).then((response) => {
      const dynamicKeys = categ?.isDynamic && categ.dynamicCriteria ?
        new Set(categ.dynamicCriteria.split('|').map((f: string) => f.split(':')[0])) :
        new Set();
      const filters = dynamicKeys.size > 0 ?
        response.filters.filter((f: { nameId: string }) => !dynamicKeys.has(f.nameId)) :
        response.filters;
      setViewData((prev) => ({ ...prev, filters }));
    });
  }, [categId]);

  const handleDeleteAll = async () => {
    await refreshView({ page: viewData.page || 1, sort: viewData.sort, filterQuery: '', onlyCU: onlyCUChecked });
  };

  const handleFilterChange = async (changes) => {
    let newFilter = changes.filterQuery ?? filterQuery;
    const sort = +(changes.sort ?? viewData.sort ?? 1);
    const page = +(changes.nameId ? 1 : changes.page ?? viewData.page ?? 1);

    if (changes.nameId) {
      const { nameId, valueId, check, price, minPrice, maxPrice } = changes;
      if (price || nameId === 'Price') {
        newFilter = updateFilterString(newFilter, 'minPrice', minPrice, !!minPrice || minPrice === 0);
        newFilter = updateFilterString(newFilter, 'maxPrice', maxPrice, !!maxPrice);
      } else if (isNote(nameId)) {
        newFilter = updateFilterString(newFilter, nameId, valueId, false);
        newFilter = updateFilterString(newFilter, nameId, valueId, true);
      } else {
        newFilter = updateFilterString(newFilter, nameId, valueId, check);
      }
    }

    await refreshView({ page: page, sort: sort, filterQuery: newFilter, onlyCU: changes.onlyCU ?? onlyCUChecked });
  };

  const computeShouldHaveShadow = (element: HTMLDivElement) => {
    if (!element) return;
    const topOffset = parseInt(getComputedStyle(element).top, 10) || 0;

    const onScroll = () => {
      if (element.getBoundingClientRect().top <= topOffset) {
        element.classList.add('shadow');
        return;
      }
      element.classList.remove('shadow');
    };
    document.addEventListener('scroll', onScroll, { passive: true });
  };

  const currentItemDraft = (product) => getCurrentProductItem(
    product, filterQuery, viewData.sort, viewData.categ, false, storeId, false, isCUCategory
  );

  const showCUProducts = () => {
    const changes = { nameId: ATTRIBUTES.CONDITIONING, valueId: FILTERS.ONLY_CU };
    handleFilterChange(!onlyCUChecked ?
      { ...changes, onlyCU: true, check: true } :
      { ...changes, onlyCU: false, check: false }
    );
  };

  const goListingLayout = async () => {
    const queryParams = [
      viewData.page !== 1 && `page=${viewData.page}`,
      viewData.sort !== 1 && `sort=${viewData.sort}`,
      filterQuery !== '' && `filter=${filterQuery}`,
      'listingView=true'
    ].filter((p) => !!p).join('&');

    let url: string = router.asPath.split('?')[0];

    if (queryParams) {
      url += `${url.includes('?') ? '&' : '?'}${queryParams}`;
    }

    await router.push(url, url);
  };

  return (
    <>
      <MetaTagsMillesima
        categ={viewData.categ}
        nbTotalRows={viewData.nbTotalRows}
        page={viewData.page}
        pageSize={pageSize}
        originalUrl={baseUrl}
        isMobileDevice={isMobileDevice}
      />

      <div className={styles.container}>
        {isDesktop && (
          <BreadcrumbContainer
            homeLabel={t('breadcrumbHomeLabel')}
            {...viewData?.categ}
            baseUrl={isDesktop ? url : baseUrl}
          />
        )}
        <CategHeader
          categ={viewData?.categ}
          descriptionRef={descriptionRef}
          isDesktop={isDesktop}
          isMobileDevice={isMobileDevice}
        />
        <div className={styles['total-products']} ref={topListingRef}>
          <span>{t('category:results.total_plural', { count: viewData.nbTotalRows })}</span>
          {!isCUCategory && !isMobile && (
            <Toggle
              iconRight={{
                src: URLS.LIST_VIEW_OUTLINED, width: 16, height: 16, alt: 'change layout', onClick: goListingLayout,
              }}
              iconLeft={{
                src: URLS.GRID_VIEW_FILLED, width: 16, height: 16, alt: 'current layout'
              }}
            />
          )}
        </div>
      </div>

      <div className={styles['sticky-container']} ref={(e) => computeShouldHaveShadow(e)}>
        <div className={styles['filter-button-container']}>
          <CategFilters
            onFilterChange={handleFilterChange}
            deleteFilter={handleFilterChange}
            deleteAll={handleDeleteAll}
            selectedFiltersInfos={selectedFiltersInfos}
            filters={viewData.filters ?? []}
            onlyCUChecked={onlyCUChecked}
            showCUProducts={showCUProducts}
            isFromWB={false}
            isFromCU={isCUCategory}
            sort={viewData.sort}
            isSwitchDisable={isSwitchDisable}
          />
          {isDesktop && (
            <div className={styles.sort}>
              <SortDesktop
                selectedSort={viewData.sort}
                onSortChange={(s) => handleFilterChange({ sort: s })}
                isProducerCategory={false}
                t={t}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.container}>
        {selectedFiltersInfos?.length > 0 && isDesktop && (
          <SelectedFilters
            selectedFiltersInfos={selectedFiltersInfos}
            handleDelete={handleFilterChange}
            handleDeleteAll={handleDeleteAll}
          />
        )}
        <ProductListCU
          productList={viewData?.products}
          computeCurrentItem={currentItemDraft}
          espotPush={categ?.espotListingPush}
          visibilityTracking={visibilityTracking}
          isCategCu={categId === SEO_TYPES.CATEG_CU_PREFIX_ID}
        />
        {viewData.nbTotalRows > pageSize && (
          <Pagination
            t={t}
            categ={viewData.categ}
            nbTotalRows={viewData.nbTotalRows}
            page={viewData.page}
            pageSize={pageSize}
            filterQuery={filterQuery}
            onChange={(page) => handleFilterChange({ page })}
            isMobile={!isDesktop}
            customClassName={styles.pagination}
          />
        )}
        {topCategs && (
          <div className={styles['top-category']}>
            <TopCategory
              t={t}
              topCategory={topCategs}
              isMobileDevice={true}
              numberOfLinks={topCategs.length}
              customClassName={{ title: styles.title, list: styles.list }}
            />
          </div>
        )}
        <CategFooter
          categ={viewData.categ}
          isDesktop={isDesktop}
          isMobileDevice={isMobileDevice}
          descriptionRef={descriptionRef}
        />
        {!isDesktop && (
          <BreadcrumbContainer
            homeLabel={t('breadcrumbHomeLabel')}
            {...viewData.categ}
            baseUrl={isDesktop ? url : baseUrl}
          />
        )}
      </div>
      {isFilterMobileVisible && viewData.filters &&
        <FilterMobileLike
          showCUProducts={showCUProducts}
          onlyCUChecked={onlyCUChecked}
          filters={viewData.filters}
          selectedFiltersInfos={selectedFiltersInfos}
          deleteAll={handleDeleteAll}
          onChange={handleFilterChange}
          onDelete={handleFilterChange}
        />
      }
      {isSortMobileVisible &&
        <SortMobile
          selectedSort={viewData.sort}
          onChange={(s) => handleFilterChange({ sort: s })}
          isProducerCategory={false}
        />
      }
      {alertPrimeurInfosModalVisible && (
        <ModalAlertPrimeurInfos isOpen={alertPrimeurInfosModalVisible} />
      )}
    </>
  );
};

export default CategViewGrid;

/**
  * Prefetch function  called on server side
  * @param {object} ctx The server side context
  * @param {object} store The server side application store
  * @param {object} props The seo page props
  * @return {object} Return an object containing the component props
  *    layoutOption : object containing the needed page layout (see _app component)
  *    namespaces : an array containing the needed translation namespaces
  */
export const prefetchView = async (ctx: GetServerSidePropsContext, { getState, services }, props) => {
  const { query, resolvedUrl } = ctx;
  const {
    root: { baseUrl },
    cart: { content: { storeIdForPrice: storeIdForPriceInCart } },
    gui: { storeIdForPrice: storeIdForPriceInStore }
  } = getState();
  const params = new URLSearchParams(resolvedUrl.split('?')[1]);
  const { productsService } = services;
  const { storeId } = props;
  const storeIdForPrice = storeIdForPriceInStore || storeIdForPriceInCart;

  const { hashUser: hashUserFromQuery } = query;
  const normalize = (value, def) => value ? +value : def;
  const sort = normalize(params.get('sort'), DEFAULT_SORT);
  const page = normalize(params.get('page'), DEFAULT_PAGE);
  const filterQuery = params.get('filter') || '';

  let categId;

  if (props?.id) {
    categId = props.id.includes(`-${storeId}`) ? props.id.replace(`-${storeId}`, '') : props.id;
  } else {
    const stateCateg = getState().categ;
    categId = stateCateg.categId;
  }

  const onlyCUChecked = filterQuery?.includes('conditionnement:onlyCU');

  const categInfos = await productsService.getAllBottlesProductAndCategForListingCU(
    {
      storeId,
      categ: categId,
      page,
      size: PAGE_SIZE.LIST_CU,
      withFilter: (filterQuery && filterQuery !== '') ? 'true' : 'false',
      isCU: categId?.includes(SEO_TYPES.CATEG_CU_PREFIX_ID) || onlyCUChecked
    },
    storeIdForPrice
  );

  const namespacesRequired = [NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER];

  return {
    layoutOptions: {
      withScrollButton: true
    },
    namespacesRequired,
    page,
    sort,
    originalUrl: baseUrl,
    products: categInfos?.products,
    categ: categInfos?.categ,
    topCategs: categInfos?.top,
    categId: categId === categInfos?.categ?.groupIdentifier ? categId : categInfos?.categ?.groupIdentifier,
    nbTotalRows: categInfos?.nbTotalRows,
    hashUserFromQuery
  };
};
